import {
  apiErrorMessage,
  fetchQuestionnaireResponses,
  fetchResponseFilterOptions,
  removeQuestionnaireResponse,
  reportPdfUrl,
  responsesCsvUrl
} from '@api/taptotip/questionnaire';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CARD } from './Editor';
import { InsightsHeadline } from './Insights';
import {
  LANGUAGE_NAMES,
  RATING_FILTERS,
  answeredQuestions,
  emptyFilters,
  excerpt,
  formatDeparture,
  formatSubmitted,
  itineraryRatings,
  needsAttention,
  ratingWord,
  toQuery
} from './responses';

/**
 * Card 325 — what guests said, for head office.
 *
 * John's brief (RIT): feedback was arriving too late, negative feedback was
 * being binned by Tour Managers, and someone had to read every paper form to
 * find the comments. So the page leads with two panels, unhappy guests and
 * delighted ones, and puts the comment first everywhere a response appears.
 * It refreshes itself every 30 seconds, so a complaint made on the coach this
 * afternoon is on screen this afternoon.
 */

const REFRESH_MS = 30000;
const PAGE_SIZE = 50;
const PANEL_SIZE = 5;

// Filters: one height, one label style, everywhere (card 325 tidy-up).
const SELECT =
  'h-10 w-full text-sm text-slate-900 border border-solid border-slate-300 rounded-xl px-3 bg-white min-w-0 disabled:bg-slate-50 disabled:text-slate-400';
const FIELD = 'flex flex-col gap-1 min-w-0';
const LABEL = 'text-xs font-bold text-slate-500';
const DOWNLOAD =
  'h-10 inline-flex items-center text-sm font-bold rounded-xl px-4 border border-solid border-slate-300 bg-white text-slate-700 no-underline hover:border-slate-400 hover:text-slate-900 transition-colors';

const SCORE_TONE = {
  negative: 'bg-rose-50 text-rose-700',
  neutral: 'bg-slate-100 text-slate-700',
  positive: 'bg-emerald-50 text-emerald-700'
};

const Score = ({ response }) => (
  <span
    className={`inline-flex items-center text-xs font-bold rounded-lg px-2 py-1 whitespace-nowrap ${SCORE_TONE[
      response.rating
    ] || SCORE_TONE.neutral}`}>
    {response.overallScore} {ratingWord(response.overallScore)}
  </span>
);

Score.propTypes = { response: PropTypes.object.isRequired };

// Only with a comment to check: a stale check must never describe words that
// are not on screen.
const hasCommentMismatch = response =>
  Boolean(response.comment && response.llmCheck && response.llmCheck.mismatch);

const CheckFlag = ({ response }) =>
  hasCommentMismatch(response) ? (
    <span
      className="text-xs font-bold rounded-lg px-2 py-1 bg-amber-50 text-amber-800 whitespace-nowrap"
      title={response.llmCheck.reason || ''}>
      Check comment
    </span>
  ) : null;

CheckFlag.propTypes = { response: PropTypes.object.isRequired };

/**
 * Card 337 — John: head office should know feedback came from a tour that is
 * still on the road, and that a guest filling in over several days may add
 * more. Both are labels, never filters on what counts.
 */
const LiveFlags = ({ response }) => (
  <>
    {response.liveTour && (
      <span
        className="text-xs font-bold rounded-lg px-2 py-1 bg-sky-50 text-sky-800 whitespace-nowrap"
        title="This tour is still on the road, so there may be time to act"
        data-testid="flag-live-tour">
        Live tour
      </span>
    )}
    {response.status === 'in_progress' && (
      <span
        className="text-xs font-bold rounded-lg px-2 py-1 border border-solid border-slate-300 text-slate-600 whitespace-nowrap"
        title="The guest has not finished the form yet and may add more"
        data-testid="flag-in-progress">
        In progress
      </span>
    )}
  </>
);

LiveFlags.propTypes = { response: PropTypes.object.isRequired };

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl bg-white border border-solid border-slate-200 px-5 py-4 min-w-0">
    <div className="text-xs font-bold text-slate-500">{label}</div>
    <div className="text-2xl font-black text-slate-900 mt-1 tabular-nums" data-testid={testId}>
      {value}
    </div>
  </div>
);

Stat.propTypes = { label: PropTypes.string, value: PropTypes.node, testId: PropTypes.string };

const Panel = ({ title, empty, rows, onOpen, testId }) => (
  <section className={CARD} data-testid={testId}>
    <h2 className="text-base font-black text-slate-900 m-0 mb-3">{title}</h2>
    {rows.length === 0 ? (
      <p className="text-sm text-slate-500 m-0">{empty}</p>
    ) : (
      <ul className="list-none m-0 p-0 flex flex-col gap-3">
        {rows.map(row => (
          <li key={row._id}>
            <button
              type="button"
              onClick={() => onOpen(row)}
              className="w-full text-left bg-transparent border-0 p-0 cursor-pointer group">
              <div className="flex flex-wrap items-center gap-2">
                <Score response={row} />
                <CheckFlag response={row} />
                <LiveFlags response={row} />
                <span className="text-xs text-slate-500">
                  {row.tourName}, {formatDeparture(row.departureDate)}
                </span>
              </div>
              <p className="text-sm text-slate-800 m-0 mt-1 group-hover:underline">
                {row.comment ? `“${excerpt(row.comment, 160)}”` : 'No comment left.'}
              </p>
            </button>
          </li>
        ))}
      </ul>
    )}
  </section>
);

Panel.propTypes = {
  title: PropTypes.string,
  empty: PropTypes.string,
  rows: PropTypes.array,
  onOpen: PropTypes.func,
  testId: PropTypes.string
};

const RatedList = ({ title, rows }) =>
  rows.length ? (
    <div className="mt-5">
      <h3 className="text-sm font-black text-slate-900 m-0 mb-2">{title}</h3>
      <dl className="m-0 grid grid-cols-[3fr_2fr] gap-x-4 gap-y-1.5 text-sm">
        {rows.map(row => (
          <React.Fragment key={row.id}>
            <dt className="text-slate-700">
              {row.name}
              {row.detail && <span className="text-slate-500">, {row.detail}</span>}
            </dt>
            <dd
              className={`m-0 font-bold text-right ${
                row.score !== null && row.score <= 2 ? 'text-rose-700' : 'text-slate-900'
              }`}>
              {row.text}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  ) : null;

RatedList.propTypes = { title: PropTypes.string, rows: PropTypes.array };

const REMOVAL_REASONS = [
  { value: 'guest_request', label: 'The guest asked us to delete it' },
  { value: 'not_genuine', label: 'Not a real guest, such as a test or a mistake' }
];

/**
 * Andy, 2026-10-07: head office takes a response out, for a guest who asks to
 * be erased or one that was never a real guest. Two steps, because it cannot
 * be undone, and the second says exactly what goes, including that emails
 * already sent stay sent.
 */
const RemoveResponse = ({ responseId, onRemoved }) => {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!asking) {
    return (
      <button
        type="button"
        onClick={() => setAsking(true)}
        className="text-sm font-bold text-rose-700 bg-transparent border-0 p-0 cursor-pointer hover:underline"
        data-testid="remove-response">
        Remove this response
      </button>
    );
  }

  const remove = () => {
    setBusy(true);
    setError('');
    removeQuestionnaireResponse(responseId, reason)
      .then(() => onRemoved(responseId))
      .catch(err => {
        setError(apiErrorMessage(err));
        setBusy(false);
      });
  };

  return (
    <div
      className="rounded-2xl border border-solid border-rose-200 bg-rose-50 p-4"
      role="group"
      aria-labelledby="remove-response-title"
      data-testid="remove-response-confirm">
      <h3 id="remove-response-title" className="text-sm font-black text-slate-900 m-0">
        Remove this response?
      </h3>
      <p className="text-sm text-slate-700 leading-relaxed m-0 mt-1">
        This erases the guest&rsquo;s name, email, comments and answers, and takes the response out
        of every count, list, download, digest and the insights. It can&rsquo;t be undone, and alert
        emails already sent can&rsquo;t be recalled.
      </p>
      <fieldset className="border-0 p-0 m-0 mt-3">
        <legend className="text-xs font-bold text-slate-600 p-0 mb-1.5">Why?</legend>
        <div className="flex flex-col gap-1.5">
          {REMOVAL_REASONS.map(r => (
            <label
              key={r.value}
              className="flex items-start gap-2 text-sm text-slate-800 cursor-pointer">
              <input
                type="radio"
                name="removal-reason"
                value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
                className="mt-0.5 accent-rose-600"
                data-testid={`remove-reason-${r.value}`}
              />
              {r.label}
            </label>
          ))}
        </div>
      </fieldset>
      {error && (
        <p className="text-sm text-rose-800 m-0 mt-3" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <button
          type="button"
          disabled={!reason || busy}
          onClick={remove}
          className="text-sm font-bold rounded-xl px-4 py-2 cursor-pointer border-0 bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 disabled:cursor-default"
          data-testid="remove-response-confirm-button">
          {busy ? 'Removing' : 'Remove response'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setAsking(false);
            setReason('');
            setError('');
          }}
          className="text-sm font-bold rounded-xl px-3 py-2 cursor-pointer border-0 bg-transparent text-slate-600 hover:text-slate-900">
          Keep it
        </button>
      </div>
    </div>
  );
};

RemoveResponse.propTypes = {
  responseId: PropTypes.string.isRequired,
  onRemoved: PropTypes.func.isRequired
};

const Drawer = ({ response, onClose, onRemoved }) => {
  const closeRef = useRef(null);
  useEffect(() => {
    if (closeRef.current) closeRef.current.focus();
    const onKey = e => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const questions = answeredQuestions(response);
  const { hotels, experiences } = itineraryRatings(response);
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Guest response">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/30 border-0 cursor-default"
        aria-label="Close"
        onClick={onClose}
      />
      <aside
        className="relative w-full max-w-lg h-full overflow-y-auto bg-white shadow-xl p-6"
        data-testid="response-drawer">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-black text-slate-900 m-0">{response.tourName}</h2>
            <p className="text-sm text-slate-500 m-0 mt-1">
              Departed {formatDeparture(response.departureDate)}, guide{' '}
              {response.guideName || 'unknown'}
            </p>
            <p className="text-sm text-slate-500 m-0">
              Sent {formatSubmitted(response.submittedAt)}, in{' '}
              {LANGUAGE_NAMES[response.locale] || response.locale}
            </p>
            {response.updatedAt &&
              new Date(response.updatedAt).getTime() - new Date(response.submittedAt).getTime() >
                60000 && (
                <p className="text-sm text-slate-500 m-0" data-testid="drawer-updated">
                  Last added to {formatSubmitted(response.updatedAt)}
                </p>
              )}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="text-sm font-bold rounded-xl px-3 py-1.5 cursor-pointer border border-solid border-slate-200 bg-white text-slate-600">
            Close
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Score response={response} />
          <CheckFlag response={response} />
          <LiveFlags response={response} />
        </div>
        {hasCommentMismatch(response) && response.llmCheck.reason && (
          <p className="text-xs text-amber-800 m-0 mt-2">{response.llmCheck.reason}</p>
        )}

        <div className="mt-5">
          <h3 className="text-sm font-black text-slate-900 m-0 mb-2">Comments</h3>
          {response.comment ? (
            <blockquote className="m-0 text-base text-slate-900 whitespace-pre-wrap border-0 border-l-4 border-solid border-indigo-200 pl-4">
              {response.comment}
            </blockquote>
          ) : (
            <p className="text-sm text-slate-500 m-0">No comment left.</p>
          )}
        </div>

        {questions.length > 0 && (
          <div className="mt-5">
            <h3 className="text-sm font-black text-slate-900 m-0 mb-2">Answers</h3>
            <dl className="m-0 grid grid-cols-[3fr_2fr] gap-x-4 gap-y-1.5 text-sm">
              {questions.map(q => (
                <React.Fragment key={q.id}>
                  <dt className="text-slate-700">{q.label}</dt>
                  <dd className="m-0 font-bold text-slate-900 text-right">{q.text}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        )}

        <RatedList title="Hotels" rows={hotels} />
        <RatedList title="Visits and experiences" rows={experiences} />

        <div className="mt-5">
          <h3 className="text-sm font-black text-slate-900 m-0 mb-2">Guest</h3>
          {response.contact && (response.contact.name || response.contact.email) ? (
            <p className="text-sm text-slate-800 m-0">
              {response.contact.name || 'No name given'}
              {response.contact.email && (
                <>
                  {', '}
                  <a href={`mailto:${response.contact.email}`} className="text-indigo-700">
                    {response.contact.email}
                  </a>
                </>
              )}
            </p>
          ) : (
            <p className="text-sm text-slate-500 m-0">Left no name or email.</p>
          )}
        </div>

        {onRemoved && (
          <div className="mt-8 pt-5 border-0 border-t border-solid border-slate-100">
            <RemoveResponse key={response._id} responseId={response._id} onRemoved={onRemoved} />
          </div>
        )}
      </aside>
    </div>
  );
};

Drawer.propTypes = {
  response: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  // Present where the list behind can drop the response afterwards.
  onRemoved: PropTypes.func
};

const Responses = ({ onOpenInsights, initialFilters, drillLabel, onClearDrill }) => {
  const [filters, setFilters] = useState(() => ({ ...emptyFilters(), ...(initialFilters || {}) }));
  // What Insights sent us here to look at, until the filters are cleared.
  const [drill, setDrill] = useState(drillLabel || '');
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState({ tours: [], guides: [], departures: [] });
  const [data, setData] = useState(null);
  const [panels, setPanels] = useState({ attention: [], highlights: [] });
  const [status, setStatus] = useState('loading');
  const [open, setOpen] = useState(null);

  const hasFilters = Object.values(filters).some(Boolean);

  const set = patch => {
    setPage(1);
    setFilters(current => {
      const next = { ...current, ...patch };
      if (patch.tourId !== undefined && patch.tourId !== current.tourId) next.departureDate = '';
      return next;
    });
  };

  // Departures are listed per tour, so the options follow the tour filter.
  useEffect(() => {
    fetchResponseFilterOptions(filters.tourId ? { tourId: filters.tourId } : {})
      .then(result => setOptions(result || { tours: [], guides: [], departures: [] }))
      .catch(() => {});
  }, [filters.tourId]);

  const load = useCallback(
    (quiet = false) => {
      if (!quiet) setStatus('loading');
      const withoutRating = { ...filters, rating: '' };
      return Promise.all([
        fetchQuestionnaireResponses(toQuery(filters, { page, pageSize: PAGE_SIZE })),
        fetchQuestionnaireResponses(
          toQuery(withoutRating, { rating: 'check', pageSize: PANEL_SIZE })
        ),
        fetchQuestionnaireResponses(
          toQuery(withoutRating, { rating: 'positive', pageSize: PANEL_SIZE })
        )
      ])
        .then(([list, attention, highlights]) => {
          setData(list);
          setPanels({ attention: attention.rows, highlights: highlights.rows });
          setStatus('ready');
        })
        .catch(() => {
          if (!quiet) setStatus('error');
        });
    },
    [filters, page]
  );

  useEffect(() => {
    load();
  }, [load]);

  // New answers arrive while head office is looking; pick them up quietly.
  useEffect(() => {
    const timer = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const totals = data ? data.totals : null;
  const pages = totals ? Math.max(1, Math.ceil(totals.count / PAGE_SIZE)) : 1;
  const tourName = id => (options.tours.find(t => t.id === id) || {}).name || '';

  return (
    <div className="flex flex-col gap-6" data-testid="responses">
      {drill ? (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-indigo-50 border border-solid border-indigo-100 px-4 py-3"
          role="status"
          data-testid="drill-banner">
          <p className="text-sm text-slate-800 m-0">
            <span className="font-bold">From Insights:</span> {drill}
          </p>
          <div className="flex items-center gap-2">
            {onOpenInsights && (
              <button
                type="button"
                onClick={onOpenInsights}
                className="h-9 text-sm font-bold px-3 rounded-xl bg-white border border-solid border-indigo-200 text-indigo-700 cursor-pointer"
                data-testid="drill-back">
                Back to Insights
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setPage(1);
                setFilters(emptyFilters());
                setDrill('');
                if (onClearDrill) onClearDrill();
              }}
              className="h-9 text-sm font-bold px-3 rounded-xl bg-transparent border-0 text-slate-600 hover:text-slate-900 cursor-pointer"
              data-testid="drill-clear">
              Show all responses
            </button>
          </div>
        </div>
      ) : (
        onOpenInsights && <InsightsHeadline onOpen={onOpenInsights} />
      )}
      {/* One panel, two rows: what to look at (four equal-height fields on a
          grid), then how to slice it and what to take away. Every control is
          40px tall and every label the same style, so nothing wraps out of line. */}
      <section
        className="rounded-2xl bg-white border border-solid border-slate-200 p-4 sm:p-5 flex flex-col gap-4"
        aria-label="Filters"
        data-testid="filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1.6fr] gap-3">
          <label className={FIELD}>
            <span className={LABEL}>Tour</span>
            <select
              className={SELECT}
              value={filters.tourId}
              onChange={e => set({ tourId: e.target.value })}
              data-testid="filter-tour">
              <option value="">All tours</option>
              {options.tours.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className={FIELD}>
            <span className={LABEL}>Departure</span>
            <select
              className={SELECT}
              value={filters.departureDate}
              disabled={!filters.tourId}
              onChange={e => set({ departureDate: e.target.value })}
              data-testid="filter-departure">
              <option value="">{filters.tourId ? 'All departures' : 'Choose a tour first'}</option>
              {options.departures
                .filter(d => d.tourId === filters.tourId)
                .map(d => (
                  <option key={d.departureDate} value={d.departureDate}>
                    {formatDeparture(d.departureDate)} ({d.count})
                  </option>
                ))}
            </select>
          </label>
          <label className={FIELD}>
            <span className={LABEL}>Guide</span>
            <select
              className={SELECT}
              value={filters.guideId}
              onChange={e => set({ guideId: e.target.value })}
              data-testid="filter-guide">
              <option value="">All guides</option>
              {options.guides.map(g => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
          <fieldset className={`${FIELD} border-0 p-0 m-0`}>
            <legend className={`${LABEL} p-0 mb-1`}>Dates</legend>
            <div className="flex items-center gap-2 min-w-0">
              <input
                type="date"
                aria-label="From"
                className={`${SELECT} flex-1`}
                value={filters.from}
                onChange={e => set({ from: e.target.value })}
                data-testid="filter-from"
              />
              <span className="text-xs text-slate-400" aria-hidden="true">
                to
              </span>
              <input
                type="date"
                aria-label="To"
                className={`${SELECT} flex-1`}
                value={filters.to}
                onChange={e => set({ to: e.target.value })}
                data-testid="filter-to"
              />
            </div>
          </fieldset>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-0 border-t border-solid border-slate-100">
          <div
            className="inline-flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1"
            role="radiogroup"
            aria-label="Show">
            {RATING_FILTERS.map(r => (
              <button
                key={r.value || 'all'}
                type="button"
                role="radio"
                aria-checked={filters.rating === r.value}
                onClick={() => set({ rating: r.value })}
                data-testid={`filter-rating-${r.value || 'all'}`}
                className={`h-8 text-sm font-bold rounded-lg px-3 cursor-pointer border-0 transition-colors ${
                  filters.rating === r.value
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'bg-transparent text-slate-600 hover:text-slate-900'
                }`}>
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {hasFilters && (
              <button
                type="button"
                onClick={() => {
                  setPage(1);
                  setFilters(emptyFilters());
                  setDrill('');
                  if (onClearDrill) onClearDrill();
                }}
                className="h-10 text-sm font-bold px-2 bg-transparent border-0 text-slate-500 hover:text-slate-900 cursor-pointer"
                data-testid="filters-clear">
                Clear filters
              </button>
            )}
            <a
              href={responsesCsvUrl(toQuery(filters))}
              download
              className={DOWNLOAD}
              data-testid="download-csv">
              Download CSV
            </a>
            <a
              href={reportPdfUrl(toQuery(filters))}
              download
              className={DOWNLOAD}
              data-testid="download-pdf">
              Download PDF
            </a>
          </div>
        </div>
      </section>

      {status === 'error' && (
        <div className={CARD}>
          <p className="text-slate-600 m-0 mb-4">We couldn&rsquo;t load the responses.</p>
          <button
            type="button"
            onClick={() => load()}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white border-0 cursor-pointer">
            Try again
          </button>
        </div>
      )}

      {status !== 'error' && totals && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Responses" value={totals.count} testId="stat-count" />
            <Stat
              label="Average overall"
              value={
                totals.averageOverall === null ? '-' : `${totals.averageOverall.toFixed(1)} / 5`
              }
              testId="stat-average"
            />
            <Stat
              label="Would recommend"
              value={
                totals.recommendRate === null ? '-' : `${Math.round(totals.recommendRate * 100)}%`
              }
              testId="stat-recommend"
            />
            <Stat label="Needs attention" value={totals.needsAttention} testId="stat-attention" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel
              title="Needs attention"
              empty="Nothing here. No unhappy guests for these filters."
              rows={panels.attention}
              onOpen={setOpen}
              testId="panel-attention"
            />
            <Panel
              title="Highlights"
              empty="No delighted guests for these filters yet."
              rows={panels.highlights}
              onOpen={setOpen}
              testId="panel-highlights"
            />
          </div>

          <section className={`${CARD} !p-0 overflow-hidden`}>
            {data.rows.length === 0 ? (
              <p className="text-sm text-slate-500 m-0 p-6" data-testid="responses-empty">
                {totals.count === 0 && !Object.values(filters).some(Boolean)
                  ? 'No responses yet. They appear here as soon as a guest sends one.'
                  : 'No responses match these filters.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table
                  className="w-full min-w-[900px] text-sm border-collapse"
                  data-testid="responses-table">
                  <thead>
                    <tr className="text-left text-xs font-bold text-slate-500">
                      <th className="px-4 py-3">Sent</th>
                      <th className="px-4 py-3 min-w-[170px]">Tour and departure</th>
                      <th className="px-4 py-3 min-w-[130px]">Guide</th>
                      <th className="px-4 py-3">Overall</th>
                      <th className="px-4 py-3 min-w-[300px]">Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map(row => (
                      <tr
                        key={row._id}
                        onClick={() => setOpen(row)}
                        className={`cursor-pointer border-0 border-t border-solid border-slate-100 hover:bg-slate-50 ${
                          needsAttention(row) ? 'bg-rose-50/40' : ''
                        }`}
                        data-testid={`row-${row._id}`}>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                          {formatSubmitted(row.submittedAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-900 font-bold">
                            {row.tourName || tourName(row.tourId)}
                          </div>
                          <div className="text-xs text-slate-500">
                            {formatDeparture(row.departureDate)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{row.guideName}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col items-start gap-1">
                            <Score response={row} />
                            <CheckFlag response={row} />
                            <LiveFlags response={row} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-800">
                          {row.comment ? (
                            excerpt(row.comment)
                          ) : (
                            <span className="text-slate-400">No comment</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-0 border-t border-solid border-slate-100 text-sm">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  className="font-bold text-indigo-700 bg-transparent border-0 cursor-pointer disabled:text-slate-300">
                  Newer
                </button>
                <span className="text-slate-500">
                  Page {page} of {pages}
                </span>
                <button
                  type="button"
                  disabled={page >= pages}
                  onClick={() => setPage(p => p + 1)}
                  className="font-bold text-indigo-700 bg-transparent border-0 cursor-pointer disabled:text-slate-300">
                  Older
                </button>
              </div>
            )}
          </section>
        </>
      )}

      {status === 'loading' && !totals && (
        <div className={CARD} role="status" aria-label="Loading responses">
          <div className="grid grid-cols-4 gap-3">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-slate-100 rounded-2xl" />
            ))}
          </div>
        </div>
      )}

      {open && (
        <Drawer
          response={open}
          onClose={() => setOpen(null)}
          onRemoved={() => {
            setOpen(null);
            load(true);
          }}
        />
      )}
    </div>
  );
};

Responses.propTypes = {
  onOpenInsights: PropTypes.func,
  initialFilters: PropTypes.object,
  drillLabel: PropTypes.string,
  onClearDrill: PropTypes.func
};

// The whole response, used by the element page too (2026-10-02).
export { Drawer as ResponseDrawer };

export default Responses;
